/**
 * The wire shape `Rws.AiInsights.Host.Status.StatusEndpoints` returns (`TD-30`). The API itself is
 * `AllowAnonymousAccess`, so this module never checks for `denied` from the network call — the
 * sync-status *screen* is what enforces an administration permission, client-side, before it ever
 * renders (see `routes/admin/SyncStatusPage.tsx`), so an unclassified visitor cannot reach it just
 * because the one API call behind it happens to be public.
 */
import { adminRequest, type AdminResult } from "./adminApi";

/** One vendor's sync-freshness status. */
export interface VendorSyncStatus {
  vendorId: string;
  displayName: string;
  hasSyncedBefore: boolean;
  lastSuccessfulSyncAt: string | null;
  lastAttemptAt: string | null;
  lastAttemptOutcome: string | null;
  nextExpectedRefreshAt: string;
  expectedFreshnessSeconds: number;
  isRunning: boolean;
}

const syncStatusPath = "/api/status/sync";

/** Calls `POST /api/status/sync/{vendorId}/run`: starts a background sync of that vendor now. */
export async function runSyncNow(vendorId: string): Promise<AdminResult<undefined>> {
  return adminRequest<undefined>(`${syncStatusPath}/${encodeURIComponent(vendorId)}/run`, { method: "POST" }, "sync now");
}

/** Calls `GET /api/status/sync`. */
export async function fetchSyncStatus(): Promise<AdminResult<VendorSyncStatus[]>> {
  const result = await adminRequest<{ vendors: VendorSyncStatus[] }>(syncStatusPath, { method: "GET" }, "sync status");
  return result.kind === "ok" ? { kind: "ok", response: result.response.vendors } : result;
}

/** The most recent recorded attempt of one vendor's sync, including how it finished or why it failed. */
export interface SyncRunDetail {
  startedAt: string;
  finishedAt: string | null;
  outcome: string | null;
  changedCount: number;
  wasDryRun: boolean;
  failureDetail: string | null;
}

/** One vendor's most recent sync attempt; `lastAttempt` is null when the vendor has never been attempted. */
export interface VendorLastRun {
  vendorId: string;
  lastAttempt: SyncRunDetail | null;
}

/** Calls `GET /api/status/sync/last-runs`. Administration-only: answers 403 without `settings.write`. */
export async function fetchSyncLastRuns(): Promise<AdminResult<VendorLastRun[]>> {
  const result = await adminRequest<{ vendors: VendorLastRun[] }>(`${syncStatusPath}/last-runs`, { method: "GET" }, "sync last runs");
  return result.kind === "ok" ? { kind: "ok", response: result.response.vendors } : result;
}

/** Calls `POST /api/status/sync/{vendorId}/run-full`: starts a background sync of all of that vendor's data, not just the latest increment. */
export async function runFullSync(vendorId: string): Promise<AdminResult<undefined>> {
  return adminRequest<undefined>(`${syncStatusPath}/${encodeURIComponent(vendorId)}/run-full`, { method: "POST" }, "sync all data");
}

/** Calls `POST /api/status/sync/{vendorId}/rebuild`: forgets how far the vendor's sync has got, then re-reads all of its history in the background. */
export async function rebuildSync(vendorId: string): Promise<AdminResult<undefined>> {
  return adminRequest<undefined>(`${syncStatusPath}/${encodeURIComponent(vendorId)}/rebuild`, { method: "POST" }, "re-read all history");
}

/** One model the portal cannot price: the invocations the latest read counted (a replace-per-model snapshot), and when it was last seen. */
export interface UnpricedModel {
  modelId: string;
  invocationCount: number;
  lastSeenAt: string;
}

/**
 * One operator price override for a model AWS has no price for. `InUse` while AWS cannot price the model; `NoEffect` once the live price
 * book can (AWS always wins). Model id, rates and the operator's note only.
 */
export interface PriceOverride {
  modelId: string;
  inputPerMillion: number;
  outputPerMillion: number;
  cacheReadPerMillion: number | null;
  cacheWritePerMillion: number | null;
  source: string | null;
  reviewBy: string | null;
  state: "InUse" | "NoEffect";
  reviewDatePassed: boolean;
  invocationsPriced: number;
}

/** Invocations rejected for lacking token counts, per `model id/operation`. */
export interface RejectedInvocation {
  modelAndOperation: string;
  invocations: number;
}

/** How the daily Price List refresh last went. Carries no failure text. */
export interface PriceRefreshStatus {
  lastRunStartedAt: string | null;
  lastRunFinishedAt: string | null;
  outcome: string | null;
  hadProblems: boolean;
}

/**
 * One rate the CUR has derived for a model (AIA-109 CUR prices). `source` is `cur`. The rate is used only where AWS has no price, so it is
 * not necessarily in use. `derivedAt` is the run's time as an ISO timestamp.
 */
export interface CurPrice {
  modelId: string;
  region: string;
  source: string;
  inputPerMillion: number;
  outputPerMillion: number;
  derivedAt: string;
}

/** How the daily CUR price derivation last went, and whether its alert is raised. Carries no failure text. */
export interface CurPriceRefreshStatus {
  lastRunStartedAt: string | null;
  lastRunFinishedAt: string | null;
  outcome: string | null;
  consecutiveMissedRuns: number;
  missedRunsBeforeAlert: number;
  alertRaised: boolean;
}

/** Whether the Bedrock real-time spend estimate can be trusted (`GET /api/status/sync/bedrock-estimate`). Counts and model ids only. */
export interface BedrockEstimateStatus {
  enabled: boolean;
  enforceOnEstimate: boolean;
  lastSuccessfulReadAt: string | null;
  markerAgeSeconds: number | null;
  maxStalenessSeconds: number;
  isStale: boolean;
  consecutiveMissedRuns: number;
  missedRunsBeforeAlert: number;
  invocationsRead: number | null;
  unattributedInvocations: number | null;
  unpricedInvocations: number | null;
  rejectedInvocations: number | null;
  rejected: RejectedInvocation[];
  unpricedModels: UnpricedModel[];
  priceRefresh: PriceRefreshStatus;
  priceOverrides: PriceOverride[];
  overridePricedInvocations: number | null;
  overrideCacheUncountedInvocations: number | null;
  curPrices: CurPrice[];
  curPriceRefresh: CurPriceRefreshStatus;
}

/** Calls `GET /api/status/sync/bedrock-estimate`. Administration-only: answers 403 without `settings.write`. */
export async function fetchBedrockEstimateStatus(): Promise<AdminResult<BedrockEstimateStatus>> {
  return adminRequest<BedrockEstimateStatus>(`${syncStatusPath}/bedrock-estimate`, { method: "GET" }, "Bedrock estimate status");
}

/** Whether the regular (timer-driven) syncing is switched on. */
export interface SyncScheduleSetting {
  enabled: boolean;
}

/** Calls `GET /api/status/sync/schedule`. */
export async function fetchSyncSchedule(): Promise<AdminResult<SyncScheduleSetting>> {
  return adminRequest<SyncScheduleSetting>(`${syncStatusPath}/schedule`, { method: "GET" }, "sync schedule");
}

/** Calls `PUT /api/status/sync/schedule`. */
export async function updateSyncSchedule(enabled: boolean): Promise<AdminResult<SyncScheduleSetting>> {
  return adminRequest<SyncScheduleSetting>(`${syncStatusPath}/schedule`, { method: "PUT", body: JSON.stringify({ enabled }) }, "sync schedule update");
}